import { getShipstationShipmentCountsBatchApi, getShipstationShipmentsApi } from 'api/order';
import { shipstationShipmentActions } from 'store/slices/shipstationShipmentSlice';
import { selectShipstationEffectiveFilter } from 'store/selectors/shipstationShipmentSelectors';
import {
  getCreatedAfterForCurrentWindow,
  getStartOfToday,
  getStartOfYesterday,
} from 'store/utils/shipmentUtils';
import { handleError } from './errorHandlerThunks';

// Tracks the most recent list fetch so stale responses don't overwrite newer data.
let activeListRequestId = 0;

const PAGE_SIZE = 25;

export const fetchShipstationShipments =
  ({ page }) =>
  (dispatch, getState) => {
    // eslint-disable-next-line no-plusplus
    const requestId = ++activeListRequestId;
    const state = getState();
    const effectiveFilter = selectShipstationEffectiveFilter(state);
    const { sortBy } = state.shipstationShipment.list;
    const limit = PAGE_SIZE;
    const offset = (page - 1) * limit;

    dispatch(shipstationShipmentActions.setCurrentPage(page));
    dispatch(shipstationShipmentActions.setListLoading(true));

    return getShipstationShipmentsApi({ limit, offset, sortBy, filter: effectiveFilter })
      .then(({ count, result }) => {
        if (requestId !== activeListRequestId) return;
        dispatch(shipstationShipmentActions.loadListPage({ page, items: result, count }));
      })
      .catch((error) => {
        if (requestId !== activeListRequestId) return;
        dispatch(shipstationShipmentActions.setListLoading(false));
        dispatch(handleError({ error }));
      });
  };

export const fetchShipstationCounts = () => (dispatch, getState) => {
  const state = getState();
  const { filter } = state.shipstationShipment.list;
  // Only pass user-set non-status/non-date filters so all subcategory counts
  // are independent and reflect current search context.
  const baseFilter = {
    ...(filter.flatSearch && { flatSearch: filter.flatSearch }),
  };

  const recentCreatedAfter = getCreatedAfterForCurrentWindow();
  dispatch(shipstationShipmentActions.setCountsLoading(true));

  return getShipstationShipmentCountsBatchApi({
    queries: [
      { key: 'shippedAll', filter: { ...baseFilter, status: ['ORDER_SHIPPED'] } },
      { key: 'awaitingAll', filter: { ...baseFilter, status: ['ORDER_CREATED'] } },
      {
        key: 'awaitingRecent',
        filter: { ...baseFilter, status: ['ORDER_CREATED'], createdAfter: recentCreatedAfter },
      },
      {
        key: 'shippedToday',
        filter: { ...baseFilter, status: ['ORDER_SHIPPED'], createdAfter: getStartOfToday() },
      },
      {
        key: 'shippedYesterday',
        filter: {
          ...baseFilter,
          status: ['ORDER_SHIPPED'],
          createdAfter: getStartOfYesterday(),
          createdBefore: getStartOfToday(),
        },
      },
      {
        key: 'shippedRecent',
        filter: { ...baseFilter, status: ['ORDER_SHIPPED'], createdAfter: recentCreatedAfter },
      },
      { key: 'shippedVoided', filter: { ...baseFilter, isVoided: true } },
    ],
  })
    .then((results) => {
      const byKey = Object.fromEntries(results.map((r) => [r.key, r]));
      dispatch(
        shipstationShipmentActions.setAllCounts({
          awaitingAll: byKey.awaitingAll,
          awaitingRecent: byKey.awaitingRecent,
          shippedAll: byKey.shippedAll,
          shippedToday: byKey.shippedToday,
          shippedYesterday: byKey.shippedYesterday,
          shippedRecent: byKey.shippedRecent,
          shippedVoided: byKey.shippedVoided,
        })
      );
    })
    .catch((error) => {
      dispatch(shipstationShipmentActions.setCountsLoading(false));
      dispatch(handleError({ error }));
    });
};

export const navigateToCategory =
  ({ category }) =>
  (dispatch) => {
    // Resets subcategory + facility via setActiveCategory
    dispatch(shipstationShipmentActions.setActiveCategory(category));
    dispatch(fetchShipstationShipments({ page: 1 }));
  };

// Awaiting left panel: facility item clicked.
// If already in awaiting, keeps the active preset (subcategory). If switching from shipped,
// setActiveCategory resets subcategory + facility first, then we set the facility.
export const navigateToAwaitingFacility =
  ({ pharmacyId }) =>
  (dispatch, getState) => {
    const { activeCategory } = getState().shipstationShipment;
    if (activeCategory !== 'awaitingShipment') {
      dispatch(shipstationShipmentActions.setActiveCategory('awaitingShipment'));
    }
    dispatch(shipstationShipmentActions.setActiveFacility(pharmacyId));
    dispatch(fetchShipstationShipments({ page: 1 }));
  };

// Awaiting right preset tabs: changes time preset, keeps facility selection.
export const navigateToPreset =
  ({ preset }) =>
  (dispatch) => {
    dispatch(shipstationShipmentActions.setActiveSubcategory(preset));
    dispatch(fetchShipstationShipments({ page: 1 }));
  };

// Shipped left panel: subcategory item clicked. Resets facility via setActiveCategory.
export const navigateToShippedSubcategory =
  ({ subcategory }) =>
  (dispatch) => {
    dispatch(shipstationShipmentActions.setActiveCategory('shipped'));
    dispatch(shipstationShipmentActions.setActiveSubcategory(subcategory));
    dispatch(fetchShipstationShipments({ page: 1 }));
  };

// Shipped right facility tabs: changes facility, keeps subcategory.
export const navigateToShipstationFacility =
  ({ pharmacyId }) =>
  (dispatch) => {
    dispatch(shipstationShipmentActions.setActiveFacility(pharmacyId));
    dispatch(fetchShipstationShipments({ page: 1 }));
  };

export const updateShipstationFilter =
  ({ filter }) =>
  (dispatch) => {
    dispatch(shipstationShipmentActions.updateFilter(filter));
    dispatch(fetchShipstationShipments({ page: 1 }));
    dispatch(fetchShipstationCounts());
  };

export const resetShipstationFilter = () => (dispatch) => {
  dispatch(shipstationShipmentActions.resetFilter());
  dispatch(fetchShipstationShipments({ page: 1 }));
  dispatch(fetchShipstationCounts());
};

export const updateShipstationSortBy =
  ({ sortBy }) =>
  (dispatch) => {
    dispatch(shipstationShipmentActions.updateSortBy(sortBy));
    dispatch(fetchShipstationShipments({ page: 1 }));
  };

export const initShipstationTab = () => (dispatch) => {
  dispatch(fetchShipstationCounts());
  dispatch(fetchShipstationShipments({ page: 1 }));
};
